import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { FileText, Plus } from 'lucide-react'
import {
  Badge,
  Button,
  Can,
  Card,
  Empty,
  Input,
  Modal,
  PageHeader,
  Table,
  Textarea,
  useAuth,
  useToast,
  type Column,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import { FORMS_KEY, type FormDetail, type FormSummary } from '../../lib/form-types'

/** Formulaires de candidature (TODO phase 7). */
export function FormsPage() {
  const { session } = useAuth()
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)
  const forms = useQuery({
    queryKey: FORMS_KEY,
    queryFn: () => session.api.get<{ data: FormSummary[] }>('/admin/forms').then((r) => r.data),
  })

  const columns: Column<FormSummary>[] = [
    {
      key: 'title',
      header: 'Formulaire',
      cell: (f) => (
        <div>
          <div style={{ fontWeight: 600 }}>{f.title}</div>
          <span className="perm-code">{f.code}</span>
        </div>
      ),
    },
    {
      key: 'version',
      header: 'Version publiée',
      cell: (f) =>
        f.latestVersion ? (
          <Badge variant="success">v{f.latestVersion}</Badge>
        ) : (
          <Badge variant="neutral">Aucune</Badge>
        ),
    },
    {
      key: 'draft',
      header: 'Brouillon',
      cell: (f) => (f.hasDraft ? <Badge variant="warning">En cours</Badge> : '—'),
    },
    { key: 'competitions', header: 'Concours', numeric: true, cell: (f) => f.competitions },
  ]

  return (
    <>
      <PageHeader
        eyebrow="Pilotage"
        title="Formulaires"
        description="Formulaires de candidature configurables. Une version publiée est immuable : chaque concours (et chaque dossier) garde la version qu’il utilise."
        actions={
          <Can perm="forms.create">
            <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
              Nouveau formulaire
            </Button>
          </Can>
        }
      />
      <Card>
        <Table
          columns={columns}
          rows={forms.data ?? []}
          rowKey={(f) => f.id}
          loading={forms.isPending}
          caption="Formulaires"
          onRowClick={(f) => navigate(`/formulaires/${f.id}`)}
          empty={<Empty icon={<FileText aria-hidden="true" />} title="Aucun formulaire" />}
        />
      </Card>
      <CreateFormModal open={creating} onClose={() => setCreating(false)} />
    </>
  )
}

function CreateFormModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { session } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [form, setForm] = useState({ code: '', title: '', description: '', blank: false })
  const create = useMutation({
    mutationFn: () =>
      session.api.post<FormDetail>('/admin/forms', {
        ...form,
        description: form.description || null,
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: FORMS_KEY })
      onClose()
      navigate(`/formulaires/${created.id}`)
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(create.error)
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nouveau formulaire"
      description="Le formulaire est créé en brouillon, avec une section « Identité » de départ (ou vide)."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Annuler
          </Button>
          <Button variant="primary" type="submit" form="create-form" loading={create.isPending}>
            Créer
          </Button>
        </>
      }
    >
      <form
        id="create-form"
        className="stack"
        onSubmit={(e) => {
          e.preventDefault()
          create.mutate()
        }}
      >
        <Input
          label="Titre"
          required
          value={form.title}
          error={errors.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
        />
        <Input
          label="Code"
          required
          mono
          hint="Ex. AGENTS-2026. Identifiant stable, non modifiable."
          value={form.code}
          error={errors.code}
          onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
        />
        <Textarea
          label="Description"
          rows={2}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <label className="check-row">
          <input
            type="checkbox"
            checked={form.blank}
            onChange={(e) => setForm({ ...form, blank: e.target.checked })}
          />
          <span>Partir d’un formulaire vide (sans la section « Identité »)</span>
        </label>
      </form>
    </Modal>
  )
}
